// Types local to this helper (kept here to avoid cross-file type coupling)
type RawMonthlyData = { mission_advancement_id: number; count: number }[];
type RawYearlyData = Record<string, RawMonthlyData>;
type RawPendingData = Record<number, RawYearlyData>;

interface BarChartDataItem {
  name: string;
  fullDate: string;
  // Metric 1 (Workflow) fields
  completed?: number;
  pendingClient?: number;
  pendingDM?: number;
  pendingConsultant?: number;
  completedRaw?: number;
  pendingClientRaw?: number;
  pendingDMRaw?: number;
  pendingConsultantRaw?: number;
  // Metric 2 (Acceptance) fields
  accepted?: number;
  acceptedWithReservation?: number;
  rejected?: number;
  toBeDone?: number;
  acceptedRaw?: number;
  acceptedWithReservationRaw?: number;
  rejectedRaw?: number;
  toBeDoneRaw?: number;
  // Metric 3 (Performance) fields
  // rating99?: number;
  ratingA?: number;
  ratingB?: number;
  ratingC?: number;
  ratingD?: number;
  ratingE?: number;
  ratingS?: number;
  // rating99Raw?: number;
  ratingARaw?: number;
  ratingBRaw?: number;
  ratingCRaw?: number;
  ratingDRaw?: number;
  ratingERaw?: number;
  ratingSRaw?: number;
  // Common
  totalRaw: number;
}

// Re-implements transformPendingData as a reusable helper.
// metricId: 1 = Workflow (completed, pendingClient, pendingDM, pendingConsultant)
//           2 = Acceptance (accepted, acceptedWithReservation, rejected, toBeDone)
export default function transformPendingData(pendingData: RawPendingData, metricId: number): BarChartDataItem[] {
  const finalChartData: BarChartDataItem[] = [];

  if (!pendingData) return finalChartData;

  const MONTHS: Record<string, string> = {
    jan: '01',
    feb: '02',
    mar: '03',
    apr: '04',
    may: '05',
    jun: '06',
    jul: '07',
    aug: '08',
    sep: '09',
    oct: '10',
    nov: '11',
    dec: '12',
  };

  Object.entries(pendingData).forEach(([yearStr, yearlyData]) => {
    const year = parseInt(yearStr, 10);

    Object.entries(yearlyData).forEach(([monthStr, rawData]) => {
      if (!rawData || rawData.length === 0) return;

      let val1 = 0;
      let val2 = 0;
      let val3 = 0;
      let val4 = 0;
      let val5 = 0; // D
      let val6 = 0;
      let val7 = 0; // s

      // Metric 1: Workflow aggregation
      if (metricId === 1) {
        rawData.forEach(({ mission_advancement_id: id, count }) => {
          switch (id) {
            case 2:
            case 99:
              val1 += count; // pendingConsultant
              break;
            case 6:
            case 7:
              val2 += count; // completed
              break;
            case 3:
            case 4:
              val3 += count; // pendingClient
              break;
            case 1:
            case 5:
              val4 += count; // pendingDM
              break;
            default:
              break;
          }
        });
      } else if (metricId === 2) {
        // Metric 2: Acceptance aggregation
        rawData.forEach(({ mission_advancement_id: id, count }) => {
          switch (id) {
            case 7: // Accepted
              val1 += count;
              break;
            case 6: // Accepted with reservation
              val2 += count;
              break;
            case 5: // Rejected
              val3 += count;
              break;
            case 1:
            case 2:
            case 3: // To be done
            case 4:
              val4 += count;
              break;
            default:
              break;
          }
        });
      } else if (metricId === 3) {
        // Metric 3: Performance aggregation
        (rawData as any).forEach((item: any) => {
          const { count } = item;
          const r = item.rating;

          // if (r === 99) val1 += count;

          if (r === 'A') val2 += count;
          else if (r === 'B') val3 += count;
          else if (r === 'C') val4 += count;
          else if (r === 'D') val5 += count;
          else if (r === 'E') val6 += count;
          else if (r === 'S') val7 += count;
        });
      }

      const total = val2 + val3 + val4 + val5 + val6 + val7;
      const pct = (value: number) => (total === 0 ? 0 : Number(((value / total) * 100).toFixed(2)));

      const chartItem: BarChartDataItem = {
        name: monthStr.charAt(0).toUpperCase() + monthStr.slice(1),
        fullDate: `${year}-${MONTHS[monthStr] ?? '01'}-01`,
        totalRaw: total,
      };

      // Add metric-specific fields
      if (metricId === 1) {
        chartItem.pendingConsultant = pct(val1);
        chartItem.completed = pct(val2);
        chartItem.pendingClient = pct(val3);
        chartItem.pendingDM = pct(val4);
        chartItem.pendingConsultantRaw = val1;
        chartItem.completedRaw = val2;
        chartItem.pendingClientRaw = val3;
        chartItem.pendingDMRaw = val4;
      } else if (metricId === 2) {
        chartItem.accepted = pct(val1);
        chartItem.acceptedWithReservation = pct(val2);
        chartItem.rejected = pct(val3);
        chartItem.toBeDone = pct(val4);
        chartItem.acceptedRaw = val1;
        chartItem.acceptedWithReservationRaw = val2;
        chartItem.rejectedRaw = val3;
        chartItem.toBeDoneRaw = val4;
      } else if (metricId === 3) {
        // chartItem.rating99 = pct(val1);
        chartItem.ratingA = pct(val2);
        chartItem.ratingB = pct(val3);
        chartItem.ratingC = pct(val4);
        chartItem.ratingD = pct(val5);
        chartItem.ratingE = pct(val6);
        chartItem.ratingS = pct(val7);

        // chartItem.rating99Raw = val1;
        chartItem.ratingARaw = val2;
        chartItem.ratingBRaw = val3;
        chartItem.ratingCRaw = val4;
        chartItem.ratingDRaw = val5;
        chartItem.ratingERaw = val6;
        chartItem.ratingSRaw = val7;
      }

      finalChartData.push(chartItem);
    });
  });

  finalChartData.sort((a, b) => new Date(a.fullDate).getTime() - new Date(b.fullDate).getTime());

  return finalChartData;
}
